import React, { FunctionComponent, useState, useEffect, Dispatch } from "react";
import { makeStyles, useTheme, Theme } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import {
  FormGroup,
  FormHelperText,
  Slider,
  InputLabel,
  Input,
  MenuItem,
  Button
} from "@material-ui/core";
import ModalBuyingExpansions from "./ModalBuyingExpansions";
import Chip from "@material-ui/core/Chip";
import Select from "@material-ui/core/Select";
import Switch from "@material-ui/core/Switch";
import {
  FREE_EXPANSION,
  PREMIUM1_EXPANSION,
  PREMIUM2_EXPANSION
} from "../../../constants/expansions";
import CenteredLoader from "../../common/CenteredLoader";
import { connect, ConnectedProps } from "react-redux";
import { createGame } from "../../../redux/actions/GameActions";
import { ProfileInterface } from "../../../redux/reducers/ProfileReducers";
import { GameInterface } from "../../../redux/reducers/GameReducers";
import { ExpansionInterface } from "../../../redux/reducers/ExpansionsReducers";

const expansionsName = [FREE_EXPANSION, PREMIUM1_EXPANSION, PREMIUM2_EXPANSION];

const useStyles = makeStyles(theme => ({
  root: {
    "& > *": {
      margin: "10px",
      width: "calc(100% - 20px)",
      maxWidth: "400px"
    }
  },
  chips: {
    display: "flex",
    flexWrap: "wrap"
  },
  chip: {
    margin: 2
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  }
}));

function getStyles(name: string, expansionName: string[], theme: Theme) {
  return {
    fontWeight:
      expansionName.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium
  };
}

interface actionsType {
  createGame: (
    gameName: string,
    isPublic: boolean,
    maxPlayers: number,
    cards: string[]
  ) => (
    dispatch: Dispatch<{
      type: string;
    }>
  ) => void;
}
interface RootState {
  profile: ProfileInterface;
  game: GameInterface;
  expansion: ExpansionInterface;
}

const actionsReducers: actionsType = {
  createGame
};

const mapStateToProps = (state: RootState) => ({
  profile: state.profile,
  expansion: state.expansion,
  game: state.game
});

const connector = connect(mapStateToProps, actionsReducers);

type PropsFromRedux = ConnectedProps<typeof connector>;
interface CreateGameProps {}

const CreateGame: FunctionComponent<CreateGameProps &
  PropsFromRedux> = props => {
  const classes = useStyles();
  const [maxPlayers, setMaxPlayers] = useState(6);
  const [isPublic, setIsPublic] = useState(true);
  const [gameName, setGameName] = useState("");
  const [checked, setChecked] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [notBoughtYet, setNotBoughtYet] = useState([] as string[]);
  const [expansionList, setExpansionList] = useState([FREE_EXPANSION]);

  const cards = Object.keys(props.expansion.expansions || {})
    .filter(key => expansionList.includes(key))
    .map(value =>
      props.expansion.expansions ? props.expansion.expansions[value].images : []
    )
    .reduce((prev: string[], next: string[]) => [...prev, ...next], []);
  const handleCloseModal = () => {
    setIsOpen(false);
  };
  function checkExpansion(key: string) {
    return (
      (key === "premium1_expansion" ||
        key === "premium2_expansion" ||
        key === "premium3_expansion" ||
        key === "free_expansion") &&
      props.expansion.info &&
      props.expansion.info[key]
    );
  }
  function checkNotExpansion(key: string) {
    return (
      (key === "premium1_expansion" ||
        key === "premium2_expansion" ||
        key === "premium3_expansion" ||
        key === "free_expansion") &&
      props.expansion.info &&
      !props.expansion.info[key]
    );
  }
  useEffect(() => {
    let isCancelled = false;
    if (!isCancelled) {
      setExpansionList(expansionsName.filter(checkExpansion));
    }
    if (!isCancelled) {
      setNotBoughtYet(expansionsName.filter(checkNotExpansion));
    }
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.expansion.info]);
  const theme = useTheme();
  const handleSwitchChange = (
    event: React.ChangeEvent<{}>,
    newValue: number | number[]
  ) => {
    if (typeof newValue === "number") {
      setMaxPlayers(newValue);
    }
  };
  const handleChange = (event: any) => {
    event.target.value.filter((expansion: string) => {
      if (checkExpansion(expansion)) {
        setIsOpen(true);
        return true;
      }
      return false;
    });
    let newList = event.target.value.filter(checkExpansion);

    setExpansionList(newList);
  };

  return (
    <div>
      <h4>Create Game</h4>
      {!props.game.loading ? (
        <form className={classes.root} noValidate autoComplete="off">
          <ModalBuyingExpansions
            open={isOpen}
            onClose={handleCloseModal}
            setIsOpen={setIsOpen}
            notBoughtYet={notBoughtYet}
          />
          <FormGroup>
            <TextField
              required
              id="standard-basic"
              label="Game Name"
              value={gameName}
              error={!Boolean(gameName) && checked}
              onChange={(
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                e.preventDefault();
                setGameName(e.target.value);
              }}
            />
          </FormGroup>
          <FormGroup>
            <FormHelperText>
              {isPublic ? "Anyone can enter" : "Anyone with the link can enter"}
            </FormHelperText>
            <InputLabel>
              Public Game
              <Switch
                checked={isPublic}
                onChange={() => setIsPublic(!isPublic)}
                name="isPublic"
                color="primary"
              />
            </InputLabel>
          </FormGroup>
          <FormGroup>
            <InputLabel>Max players {maxPlayers}</InputLabel>

            <Slider
              defaultValue={6}
              aria-labelledby="discrete-slider"
              valueLabelDisplay="auto"
              step={1}
              onChange={handleSwitchChange}
              marks
              min={4}
              color="primary"
              max={8}
            />
          </FormGroup>
          <FormGroup>
            <InputLabel>Expansions</InputLabel>
            <Select
              labelId="demo-multiple-chip-label"
              id="demo-multiple-chip"
              multiple
              error={expansionList.length === 0 && checked}
              value={expansionList}
              onChange={handleChange}
              input={<Input id="select-multiple-chip" />}
              renderValue={(selected: unknown) => (
                <div className={classes.chips}>
                  {(selected as string[]).map((expansion: string) => (
                    <Chip
                      key={expansion}
                      label={displayNameExpansion(expansion)}
                      className={classes.chip}
                    />
                  ))}
                </div>
              )}
            >
              {expansionsName.map((expansion: string) => (
                <MenuItem
                  key={expansion}
                  value={expansion}
                  style={getStyles(expansion, expansionList, theme)}
                >
                  {displayNameExpansion(expansion)}
                </MenuItem>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Button
              variant="outlined"
              onClick={() => {
                setChecked(true);
                return (
                  Boolean(gameName) &&
                  expansionList.length > 0 &&
                  props.createGame(gameName, isPublic, maxPlayers, cards)
                );
              }}
              color="primary"
            >
              Create Game
            </Button>
          </FormGroup>
        </form>
      ) : (
        <CenteredLoader />
      )}
    </div>
  );
};

export default connector(CreateGame);

function displayNameExpansion(expansion: string) {
  return expansion
    .split("_")
    .map((value: string) => value[0].toUpperCase() + value.slice(1))
    .join(" ");
}
